<script lang="ts">
  import { DEFAULT_ICON } from "@shared/icons";
  import type { AppRecord, AppTheme } from "@shared/types";

  import { appsLook } from "../../lib/appsLook.svelte";
  import { errorMessage, post } from "../../lib/pb";
  import IconPicker from "../IconPicker.svelte";
  import PalettePicker from "../PalettePicker.svelte";
  import Button from "../ui/Button.svelte";
  import ErrorNote from "../ui/ErrorNote.svelte";
  import Field from "../ui/Field.svelte";
  import Input from "../ui/Input.svelte";
  import Modal from "../ui/Modal.svelte";

  let {
    open,
    onClose,
    onCreated,
  }: { open: boolean; onClose: () => void; onCreated: (app: AppRecord) => void } = $props();

  let name = $state("");
  let icon = $state(DEFAULT_ICON);
  // Sin tocar nada sigue la apariencia común de los ajustes de Planer: elegir
  // un color a mano o del catálogo es cosa de quien construye, no una
  // obligación de este formulario.
  let theme = $state<AppTheme>(appsLook.value.theme);
  let picked = $state(false);
  let busy = $state(false);
  let error = $state("");

  /** Crea la aplicación con el nombre, el icono y la visibilidad elegidos, y la abre. */
  async function submit() {
    // Intro también llega mientras el botón está bloqueado.
    if (busy || !name.trim()) return;
    busy = true;
    error = "";
    try {
      const app = await post<AppRecord>("/api/apps", {
        name,
        icon,
        // El catálogo entero desde el principio: se puede afinar después
        // en los ajustes de la aplicación, pero no hay razón para obligar
        // a un color a mano si ya se sabe con que paleta se quiere ver, ni
        // para forzar una si no se toca nada.
        // Sin elegir nada se guarda vacía y sigue a la común aunque cambie.
        theme: picked && appsLook.value.allowOwn ? theme : null,
      });
      onCreated(app);
    } catch (err) {
      error = errorMessage(err);
    } finally {
      busy = false;
    }
  }
</script>

<Modal
  id="home-create-app-modal"
  class="modal-new-app"
  width="modal-new-app-width"
  {open}
  {onClose}
  title="Crear aplicación"
  description="La aplicación tendrá una tabla y una página de inicio vacías."
>
  <div class="create-app-fields flex flex-col gap-4">
    <!--
      El icono y el nombre en un solo control (`.join`), como en los ajustes.
      Sin `Field`: su etiqueta envuelve el control, y tocar «Nombre» abriría
      el buscador de iconos, que es lo primero de dentro.
    -->
    <div class="create-app-name-field field">
      <label for="input-new-app-name">Nombre</label>
      <div class="join-new-app-identity join">
        <IconPicker bare value={icon} onChange={(next) => (icon = next)} />
        <Input
          id="input-new-app-name"
          autofocus
          bind:value={name}
          placeholder="Directorio de empleados"
          onkeydown={(e) => e.key === "Enter" && name.trim() && submit()}
        />
      </div>
    </div>

    <!-- Sin permiso para elegir, la aplicación usa la apariencia común. -->
    {#if appsLook.value.allowOwn}
      <Field label="Paleta de colores" hint="Elige los colores de la aplicación y de su icono.">
        <PalettePicker
          value={theme}
          onChange={(next) => {
            theme = next;
            picked = true;
          }}
        />
      </Field>
    {/if}

    <ErrorNote message={error} />
  </div>

  {#snippet footer()}
    <Button onclick={onClose}>Cancelar</Button>
    <Button variant="secondary" loading={busy} onclick={submit} disabled={!name.trim()}>
      Crear aplicación
    </Button>
  {/snippet}
</Modal>

<style>
  /*
   * Ancho fijo y no solo un tope: la tarjeta se ajusta a su contenido y no
   * llegaría al tope. Sin pasar del ancho de la pantalla.
   */
  :global(.modal-new-app-width) {
    width: min(40rem, 100%);
  }
</style>
